<template>
	<PageWrapper>
		<HeroesServiceArea
			:image="heroImage"
			:text="heroRichText"
		/>
		<PageDivider class="mt-4 pb-12 sm:mt-0 sm:pb-0" />
		<SectionContentContainer
			classes="mt-0 pb-12 sm:pb-16 lg:pb-0"
		>
			<template #items>
				<LazyVisibleSectionContentItem
					v-for="(item, index) in $tm('service-area.section-items')"
					:key="index"
					:copy="item"
					:reverse="false"
					:title-two-concat="areaName"
					:ui="{
						container: 'lg:items-center max-w-5xl lg:gap-[8rem]',
						title: 'text-3xl sm:text-[2.25rem] font-semibold text-left',
						titleTwo: 'text-xl sm:text-4xl font-semibold text-left max-w-sm pt-4',
						leading: 'mb-0'
					}"
					hydrate-on-visible
				>
					<template #aside>
						<div class="shadow-card rounded-xl border border-dimmed/60 flex px-8 flex-col gap-4 py-6 lg:ml-auto">
							<LogoFullColor class="w-full" />
							<div class="flex flex-col justify-center w-full">
								<UButton
									:label="$t('service-area.cta.title')"
									to="/"
									size="xl"
									class="w-full min-w-44 max-w-md mx-auto inline-flex justify-center text-center"
								/>
							</div>
							<h6 class="text-xl font-semibold text-center text-highlighted">
								{{ areaName }}
							</h6>
							<p class="text-sm max-w-44 mx-auto text-primary font-semibold text-center">
								{{ $t('service-area.cta.subtitle') }}
							</p>
						</div>
					</template>
				</LazyVisibleSectionContentItem>
			</template>
		</SectionContentContainer>
		<LazyVisibleTestimonials hydrate-on-visible />
		<LazyVisibleFaqs
			hydrate-on-visible
			:faqs="faqs"
			divider="both"
		/>
		<LazyVisibleBlogCarousel
			:id="`service-area-${areaName}`"
			hydrate-on-visible
		/>
		<LazyVisibleBannersAddressChecker hydrate-on-visible />
	</PageWrapper>
</template>

<script setup lang="ts">
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'
import {
	BASE_URL,
	COMPANY_NAME,
	COMPANY_PHONE
} from '@/constants/index.js'
import {
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_LOGO
} from '@/constants/json-ld'
import type { SvgImageProps } from '@/types/components/svg-image'
import type { HeroMarkdown } from '@/types/storyblok/hero'
import { useRoute } from 'vue-router'

const { getStory } = useStoryblokWithErrorReporting()

const route = useRoute()

const storyblok = await getStory(`service-areas/${route?.params?.area}`)

const { story } = storyblok.value?.data || {}

const {
	title, description
} = story?.content?.metatags ?? {
	title: '',
	description: ''
}

const heroImage = computed<SvgImageProps['image']>(() => {
	const img = story?.content?.cityCardContent?.[0]?.image
	return img
		? {
			id: img.id,
			url: img.filename,
			alt: img.alt
		}
		: {
			url: '',
			alt: ''
		}
})

const heroContent = story?.content?.heroContent?.[0]
const heroRichText = computed<HeroMarkdown>(() => {
	return heroContent
		? {
			title: heroContent.title,
			subtitle: heroContent?.subtitle || undefined,
			description: heroContent?.description || undefined
		}
		: {
			title: {
				type: 'doc',
				content: []
			}
		}
})

const areaName = computed(() => story?.name)

const faqs = computed(() => {
	return story?.content?.faqContent
})

const cityCardContent = computed(() => {
	return story?.content?.cityCardContent?.[0]
})

const cityImage = computed(() => {
	return cityCardContent.value?.image.filename
})

const city = computed(() => {
	if (typeof route?.params?.area === 'string') {
		return route?.params?.area?.split('-')[0] || ''
	}
	return ''
})

const cityName = computed(() => {
	return cityCardContent.value?.city
})

const stateShort = computed(() => {
	return cityCardContent.value?.stateShort
})

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogImage: () => cityImage.value,
	ogUrl: () => `${BASE_URL}${route?.fullPath}`,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => cityImage.value,
	twitterCard: () => 'summary_large_image'
})

useHead({
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			'itemListElement': [
				{
					'@type': 'ListItem',
					'position': 1,
					'name': 'Home',
					'item': BASE_URL
				}, {
					'@type': 'ListItem',
					'position': 2,
					'name': 'Service Areas',
					'item': `${BASE_URL}/service-areas`
				}, {
					'@type': 'ListItem',
					'position': 3,
					'name': city.value,
					'item': `${BASE_URL}${route?.fullPath}`
				}
			]
		},
		{
			'@type': 'Service',
			'name': COMPANY_NAME,
			'logo': JSON_LD_LOGO,
			'provider': {
				'@type': 'Organization',
				'name': COMPANY_NAME,
				'telephone': COMPANY_PHONE
			},
			'serviceType': 'High-speed Internet',
			'serviceOutput': 'Internet service',
			'areaServed': [
				{
					'@type': 'Place',
					'address': {
						'@type': 'PostalAddress',
						'addressLocality': cityName.value,
						'addressRegion': stateShort.value
					},
					'potentialAction': {
						'@type': 'SearchAction',
						'query': `Internet services in ${cityName.value}, ${stateShort.value}`
					}
				}
			],
			'hasOfferCatalog': JSON_LD_HAS_OFFER_CATALOG
		}
	]
})
</script>
